// The web app ships as a `registerType: 'autoUpdate'` PWA (see
// apps/web/vite.config.ts). When a new bundle is deployed, the auto-injected
// registration's controllerchange handler reloads the page to the new JS — BUT
// only once a new service worker is *detected*, and a service worker is only
// checked for at launch. An installed PWA (or a tab) left open across a deploy
// therefore keeps running the stale bundle indefinitely, which is why a shipped
// fix can look "not deployed" at the counter until a manual hard reload (this
// bit us on the referral overlay / logs after a staging deploy).
//
// This nudges the EXISTING registration to re-check for a new service worker
// when the app regains focus (the "reopened the PWA after a deploy" case) and
// on a slow background timer as a backstop. It deliberately uses the raw
// ServiceWorker API rather than `virtual:pwa-register` so it never registers a
// second worker alongside the one vite-plugin-pwa already auto-injects — it
// only triggers update checks; autoUpdate handles the activate + reload.
//
// No-op where there is no service worker (dev: vite-plugin-pwa devOptions are
// off, so getRegistration() resolves undefined) and where the API is absent.

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000 // hourly backstop; deploys are rare

export function startServiceWorkerUpdateChecks(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return
  }

  const check = () => {
    navigator.serviceWorker
      .getRegistration()
      .then((reg) => reg?.update())
      .catch(() => {
        // Best-effort — a failed update check (offline, transient) is a no-op;
        // the next focus/timer tick retries.
      })
  }

  // The primary trigger: the user returns to the app. Covers the common case of
  // backgrounding the installed PWA, a deploy landing, then reopening it.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check()
  })

  // Backstop for an app left open in the foreground (e.g. a staff scanner on
  // shift all day) that never fires visibilitychange.
  window.setInterval(check, UPDATE_CHECK_INTERVAL_MS)

  // One check once the worker is active, shortly after boot.
  navigator.serviceWorker.ready.then(check).catch(() => {})
}
